/**
 * Wabenflaggen (amendment 13): the first public report of the week in a hexagon plants a flag,
 * and anybody can ask whoever holds it to hand it over.
 *
 * Three rules carry the design, and each of them is a decision rather than an accident.
 *
 * **First means first to arrive, not first observed.** A report made offline reaches the
 * server later, and it loses the race to one sent at once from the same spot. Going by
 * `observed_at` instead would let anybody take any flag by backdating a report into the
 * window §14a accepts. The fairness gap that leaves is exactly what the 📵 reason is for: a
 * request is the honest way to say "I was here first, I just had no signal".
 *
 * **Only public reports claim.** A flag says, to everybody who can see the map, who reported in
 * that hexagon this week. A private or friends-only report must not be what reveals it.
 *
 * **Handing over is a transfer.** Whoever accepts a request gives the flag away entirely; it
 * is a token, not a membership list, and a flag held by three people says nothing.
 *
 * Everything here is pure. The week boundary is computed in SQL, the same way the leaderboard
 * computes it (Monday, server time), and arrives here as a `YYYY-MM-DD` string.
 */

/** The reasons a request can give. Picked, not typed — the free text is optional on top. */
export const FLAG_REASONS = [
  { key: 'home', emoji: '🏠', label: 'Ich wohne hier' },
  { key: 'local', emoji: '🍺', label: 'Mein Stammlokal' },
  { key: 'offline', emoji: '📵', label: 'War zuerst da, ohne Netz' },
  { key: 'please', emoji: '😇', label: 'Einfach so, bitte' },
] as const;

export type FlagReason = (typeof FLAG_REASONS)[number]['key'];

export function isFlagReason(value: unknown): value is FlagReason {
  return FLAG_REASONS.some((reason) => reason.key === value);
}

export function reasonOf(key: FlagReason): { key: FlagReason; emoji: string; label: string } {
  // The fallback is unreachable for a validated key. It keeps a reason renamed after requests
  // were stored from taking a whole inbox down with it.
  return FLAG_REASONS.find((reason) => reason.key === key) ?? { key, emoji: '🚩', label: key };
}

/** The same limit as a report's note (brief §12): a line, not a letter. */
export const FLAG_NOTE_MAX = 140;

/**
 * How many requests one person may have open at a time.
 *
 * Anybody may ask for any flag — "😇 Einfach so, bitte" is one of the reasons, and needing to
 * have been there would need a position history the server does not keep (brief §2). This is
 * what stops that from becoming a way to message everybody on the map at once.
 */
export const MAX_OPEN_REQUESTS = 5;

export type RequestStatus = 'open' | 'accepted' | 'declined' | 'superseded';

/**
 * What a request is, as the people involved see it.
 *
 * `expired` is derived rather than stored: an open request from a week that has ended simply
 * ran out with its flag at the Monday reset. Storing it would need a job to write a fact the
 * calendar already implies.
 */
export type RequestState = RequestStatus | 'expired';

export function requestState(
  status: RequestStatus,
  week: string,
  currentWeek: string,
): RequestState {
  if (status === 'open' && week < currentWeek) return 'expired';
  return status;
}

export const REQUEST_STATE_LABELS: Record<RequestState, string> = {
  open: 'offen',
  accepted: 'übergeben',
  declined: 'abgelehnt',
  superseded: 'ging an jemand anderen',
  expired: 'abgelaufen',
};

export type RequestInput = { reason: FlagReason; note: string | null };

export type RequestCheck = { ok: true; value: RequestInput } | { ok: false; message: string };

/** Validate what a request carries. The note is trimmed, and an empty one is no note. */
export function checkRequest(input: { reason: unknown; note: unknown }): RequestCheck {
  if (!isFlagReason(input.reason)) return { ok: false, message: 'Bitte einen Grund wählen.' };
  if (input.note !== undefined && input.note !== null && typeof input.note !== 'string') {
    return { ok: false, message: 'Die Nachricht muss Text sein.' };
  }
  const note = typeof input.note === 'string' ? input.note.trim() : '';
  if ([...note].length > FLAG_NOTE_MAX) {
    return { ok: false, message: `Höchstens ${FLAG_NOTE_MAX} Zeichen.` };
  }
  return { ok: true, value: { reason: input.reason, note: note === '' ? null : note } };
}

/** Why a request cannot be made, for the refusals the server sends back. */
export type RequestRefusal = 'no_flag' | 'yours' | 'already_asked' | 'too_many';

export const REQUEST_REFUSALS: Record<RequestRefusal, string> = {
  no_flag: 'Diese Wabe hat diese Woche keine Flagge.',
  yours: 'Die Flagge gehört schon dir.',
  already_asked: 'Du hast diese Flagge schon angefragt.',
  too_many: `Höchstens ${MAX_OPEN_REQUESTS} offene Anfragen gleichzeitig.`,
};

/** Why an answer did not go through. Each of these is somebody else having moved first. */
export type AnswerRefusal = 'not_found' | 'not_open' | 'not_holder';

export const ANSWER_REFUSALS: Record<AnswerRefusal, string> = {
  not_found: 'Diese Anfrage gibt es nicht.',
  not_open: 'Diese Anfrage ist schon erledigt.',
  not_holder: 'Die Flagge gehört inzwischen jemand anderem.',
};
